import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, HelpCircle, ShoppingBag, Truck, CreditCard, Package, Headphones, Phone, Mail, Shield, FileText } from 'lucide-react';

const Faq = () => {
  const [openSection, setOpenSection] = useState(null);
  const [openItems, setOpenItems] = useState({});

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const toggleItem = (id) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const faqSections = [
    {
      id: 'ordering',
      title: 'Ordering & Payment',
      icon: ShoppingBag,
      questions: [
        {
          id: 1,
          question: 'How do I place an order?',
          answer: 'You can place an order through our website or mobile app. Simply browse our collections, select your preferred items, add them to cart, and proceed to checkout. You can also place orders via phone by calling our customer support.'
        },
        {
          id: 2,
          question: 'What payment methods do you accept?',
          answer: 'We accept all major payment methods including credit cards, debit cards, net banking, UPI, and digital wallets like Paytm, PhonePe, and Google Pay. We also offer Cash on Delivery for select locations.'
        },
        {
          id: 3,
          question: 'Is it safe to use my credit card on your website?',
          answer: 'Yes, absolutely. We use SSL encryption and secure payment gateways to ensure all your payment information is protected. We do not store your credit card details on our servers.'
        },
        {
          id: 4,
          question: 'Can I modify or cancel my order after placing it?',
          answer: 'You can modify or cancel your order within 1 hour of placing it. For cancellations after 1 hour, please contact our customer support immediately. Once the order is processed for delivery, cancellations may not be possible.'
        }
      ]
    },
    {
      id: 'delivery',
      title: 'Delivery & Shipping',
      icon: Truck,
      questions: [
        {
          id: 5,
          question: 'Do you offer same-day delivery?',
          answer: 'Yes, we offer same-day delivery for orders placed before 2 PM in major cities. The delivery time may vary based on your location and availability of products.'
        },
        {
          id: 6,
          question: 'What are your delivery charges?',
          answer: 'We offer free delivery on all orders above ₹999. For orders below ₹999, a nominal delivery charge of ₹99 applies. Delivery charges may vary for remote locations.'
        },
        {
          id: 7,
          question: 'Can I track my order?',
          answer: 'Yes, once your order is dispatched, you will receive a tracking link via SMS and email. You can also track your order by logging into your account on our website.'
        },
        {
          id: 8,
          question: 'Do you deliver on weekends and holidays?',
          answer: 'Yes, we deliver 7 days a week, including weekends and most holidays. However, delivery timings may vary on national holidays.'
        },
        {
          id: 9,
          question: 'What if I\'m not available at the delivery address?',
          answer: 'Our delivery partner will attempt delivery twice. If you\'re not available, they will leave a missed delivery card. You can reschedule the delivery or pick it up from the nearest delivery center.'
        }
      ]
    },
    {
      id: 'products',
      title: 'Products & Quality',
      icon: Package,
      questions: [
        {
          id: 10,
          question: 'How fresh are your flowers?',
          answer: 'All our flowers are sourced directly from farms and delivered within 24 hours of being cut. We maintain strict quality control and ensure maximum freshness.'
        },
        {
          id: 11,
          question: 'Can I customize my bouquet?',
          answer: 'Yes, we offer customization options for most bouquets. You can specify color preferences, add extra flowers, or include personalized messages during checkout.'
        },
        {
          id: 12,
          question: 'Do you offer gift wrapping?',
          answer: 'Yes, we provide complimentary gift wrapping for all orders. You can also add a personalized greeting card with your message.'
        },
        {
          id: 13,
          question: 'What is your freshness guarantee?',
          answer: 'We offer a 7-day freshness guarantee on all our flowers. If your flowers don\'t last as promised, contact us within 7 days for a replacement or refund.'
        }
      ]
    },
    {
      id: 'returns',
      title: 'Returns & Refunds',
      icon: CreditCard,
      questions: [
        {
          id: 14,
          question: 'What is your return policy?',
          answer: 'We accept returns within 24 hours of delivery if the product is damaged, defective, or doesn\'t match the description. Fresh flowers are generally non-returnable unless they fail our freshness guarantee.'
        },
        {
          id: 15,
          question: 'How long does it take to process a refund?',
          answer: 'Refunds are processed within 5-7 business days after the return is approved. The time may vary depending on your bank or payment method.'
        },
        {
          id: 16,
          question: 'What if I receive damaged flowers?',
          answer: 'If you receive damaged or wilted flowers, please take photos and contact us immediately. We will arrange for replacement or provide a full refund.'
        }
      ]
    },
    {
      id: 'account',
      title: 'Account & Support',
      icon: Headphones,
      questions: [
        {
          id: 17,
          question: 'How do I create an account?',
          answer: 'You can create an account during checkout or by clicking on "Sign Up" in the top navigation. Account creation is free and gives you access to order history, tracking, and special offers.'
        },
        {
          id: 18,
          question: 'I forgot my password. How can I reset it?',
          answer: 'Click on "Forgot Password" on the login page. Enter your registered email address, and we\'ll send you a password reset link.'
        },
        {
          id: 19,
          question: 'How can I contact customer support?',
          answer: 'You can reach us via phone at 1800-123-4567, email at support@sohkinflowers.com, or through the chat support on our website. We\'re available 24/7.'
        },
        {
          id: 20,
          question: 'Do you offer corporate/bulk orders?',
          answer: 'Yes, we have special packages for corporate gifting and bulk orders. Please contact our corporate sales team at corporate@sohkinflowers.com for customized solutions.'
        }
      ]
    }
  ];

  const popularQuestions = [
    {
      id: 101,
      question: 'How far in advance should I order?',
      answer: 'For same-day delivery, order before 2 PM. For special occasions, we recommend ordering at least 2-3 days in advance to ensure availability.'
    },
    {
      id: 102,
      question: 'Do you deliver to hospitals?',
      answer: 'Yes, we deliver to hospitals. Please provide the patient\'s name, room number, and any special delivery instructions in the order notes.'
    },
    {
      id: 103,
      question: 'Can I include a personalized message?',
      answer: 'Yes, you can add a personalized message during checkout. We\'ll include it on a beautiful greeting card with your delivery.'
    },
    {
      id: 104,
      question: 'What if the recipient is not available?',
      answer: 'Our delivery team will attempt delivery twice. If unsuccessful, they\'ll contact you to arrange an alternative delivery time or location.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-pink-600 to-purple-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-6">
              <HelpCircle className="h-8 w-8" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Frequently Asked Questions</h1>
            <p className="text-xl text-pink-100 mb-8">
              Find answers to common questions about ordering, delivery, and more
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search for answers..."
                  className="w-full px-6 py-4 rounded-full text-gray-900 focus:outline-none focus:ring-4 focus:ring-pink-300"
                />
                <button className="absolute right-3 top-3 bg-pink-600 text-white p-2 rounded-full hover:bg-pink-700">
                  <HelpCircle className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Popular Questions */}
      <div className="py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8">Popular Questions</h2>
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {popularQuestions.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 cursor-pointer"
                onClick={() => toggleItem(`pop-${item.id}`)}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-lg text-gray-900">{item.question}</h3>
                  {openItems[`pop-${item.id}`] ? (
                    <ChevronUp className="h-5 w-5 text-pink-600" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </div>
                {openItems[`pop-${item.id}`] && (
                  <div className="mt-4 text-gray-600">
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Sections */}
      <div className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {faqSections.map((section) => {
              const Icon = section.icon;
              return (
                <div key={section.id} className="mb-8">
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all"
                  >
                    <div className="flex items-center">
                      <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center mr-4">
                        <Icon className="h-6 w-6 text-pink-600" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
                    </div>
                    {openSection === section.id ? (
                      <ChevronUp className="h-6 w-6 text-pink-600" />
                    ) : (
                      <ChevronDown className="h-6 w-6 text-gray-400" />
                    )}
                  </button>
                  
                  {openSection === section.id && (
                    <div className="mt-4 space-y-4">
                      {section.questions.map((item) => (
                        <div
                          key={item.id}
                          className="bg-white/80 rounded-lg p-6 shadow-sm"
                        >
                          <div
                            className="flex items-center justify-between cursor-pointer"
                            onClick={() => toggleItem(item.id)}
                          >
                            <h3 className="font-bold text-gray-900">{item.question}</h3>
                            {openItems[item.id] ? (
                              <ChevronUp className="h-5 w-5 text-pink-600" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                          {openItems[item.id] && (
                            <div className="mt-4 text-gray-600">
                              {item.answer}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Contact CTA */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-pink-600 to-purple-600 rounded-3xl p-8 md:p-12 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
            <p className="text-xl mb-8 text-pink-100">
              Our customer support team is here to help you 24/7
            </p>
            <div className="flex flex-col md:flex-row gap-6 justify-center">
              <a
                href="tel:18001234567"
                className="inline-flex items-center justify-center bg-white text-pink-700 px-8 py-4 rounded-full font-bold hover:bg-pink-50 transition-all"
              >
                <Phone className="mr-2 h-6 w-6" />
                Call: 1800-123-4567
              </a>
              <a
                href="mailto:support@sohkinflowers.com"
                className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white/10 transition-all"
              >
                <Mail className="mr-2 h-6 w-6" />
                Email Us
              </a>
            </div>
            <p className="mt-8 text-pink-200">
              Average response time: 15 minutes
            </p>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8">Related Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <Link
              to="/shipping-policy"
              className="bg-pink-50 p-6 rounded-xl text-center hover:bg-pink-100 transition-colors"
            >
              <Truck className="h-8 w-8 text-pink-600 mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">Shipping Policy</h3>
              <p className="text-gray-600 text-sm">Delivery information and charges</p>
            </Link>
            
            <Link
              to="/return-policy"
              className="bg-purple-50 p-6 rounded-xl text-center hover:bg-purple-100 transition-colors"
            >
              <CreditCard className="h-8 w-8 text-purple-600 mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">Return Policy</h3>
              <p className="text-gray-600 text-sm">Returns, refunds and exchanges</p>
            </Link>
            
            <Link
              to="/privacy-policy"
              className="bg-blue-50 p-6 rounded-xl text-center hover:bg-blue-100 transition-colors"
            >
              <Shield className="h-8 w-8 text-blue-600 mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">Privacy Policy</h3>
              <p className="text-gray-600 text-sm">How we protect your data</p>
            </Link>
            
            <Link
              to="/terms"
              className="bg-green-50 p-6 rounded-xl text-center hover:bg-green-100 transition-colors"
            >
              <FileText className="h-8 w-8 text-green-600 mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">Terms of Service</h3>
              <p className="text-gray-600 text-sm">Terms and conditions</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Faq;